import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ModalContext from 'contexts/ModalContext'
import TextButton from 'LuxKit/Button/TextButton'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import {
  GalleryButtons,
  GridContainer,
  GridImageClickable,
  PRIMARY_GALLERY_IMAGE_CLASS,
  SECONDARY_GALLERY_IMAGE_CLASS,
} from '../TripPlanDetailDrawer.styles'
import { getDrawerGalleryImageKey, useDrawerGalleryImages } from '../../hooks/useDrawerGalleryImages'

const DEFAULT_IMAGE_PARAMS: ImageParams = { tabletWidth: '400px', quality: 'good' }
const MAIN_IMAGE_PARAMS: ImageParams = { tabletWidth: '800px', quality: 'best' }
interface Props {
  images: Array<App.Image>
}

const isGridImage = (image: App.Image) => !!image.id

function OfferDetailImageGallery({ images }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const validImages = useMemo(() => {
    return images.filter((image) => image.id || image.url)
  }, [images])
  const imageSourceKey = useMemo(() => validImages.map((image) => image.id ?? image.url ?? '').join(','), [validImages])
  const {
    availableImages: lightboxImages,
    gridImages,
    imageCount: gridImageCount,
    handleImageError,
  } = useDrawerGalleryImages({
    images: validImages,
    resetKey: imageSourceKey,
    isGridImage,
  })

  const handleGalleryImageClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(e.currentTarget.dataset.index) || 0
    showModal(
      <ImageGalleryLightbox images={lightboxImages} defaultIndex={index} />,
    )
  }, [showModal, lightboxImages])

  const openGalleryLightbox = useCallback(() => {
    showModal(
      <ImageGalleryLightbox images={lightboxImages} />,
    )
  }, [showModal, lightboxImages])

  if (gridImages.length === 0) return null

  return (
    <GridContainer
      $imageCount={gridImageCount}
      $matchOfferPageAspect
    >
      {gridImages.map(({ image, lightboxIndex }, gridImageIndex) => (
        <GridImageClickable
          className={gridImageIndex === 0 ? PRIMARY_GALLERY_IMAGE_CLASS : SECONDARY_GALLERY_IMAGE_CLASS}
          key={image.id}
          onClick={handleGalleryImageClick}
          data-index={lightboxIndex}
        >
          <ResponsiveImage
            id={image.id!}
            fit="center"
            data-image-key={getDrawerGalleryImageKey(image)}
            onError={handleImageError}
            {...(gridImageIndex === 0 ? MAIN_IMAGE_PARAMS : DEFAULT_IMAGE_PARAMS)}
          />
        </GridImageClickable>
      ))}
      {lightboxImages.length > 1 && (
        <GalleryButtons direction="horizontal" gap={8} horizontalAlign="end">
          <TextButton
            kind="secondary"
            variant="dark"
            startIcon={<LineImagesIcon />}
            onClick={openGalleryLightbox}
          >
            {intl.formatMessage({
              id: 'bR2MjHBxeWo_o5ut-H9M',
              defaultMessage: 'View gallery',
              description: 'Button to open hotel image gallery lightbox in the gallery view',
            })}
          </TextButton>
        </GalleryButtons>
      )}
    </GridContainer>
  )
}

export default OfferDetailImageGallery
